import { useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AccountingPolicy, ArticleSetting } from '../models';
import type { CompanyIdentity } from '../repositories';
import { fetchCompanyCtx } from '../repositories';

interface CompanyContextProps {
  company: CompanyIdentity;
  articleSetting: ArticleSetting;
  accountingPolicy: AccountingPolicy;
  fetchContext: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextProps>({
  company: { companyCode: '', companyName: '' },
  articleSetting: ArticleSetting.init(),
  accountingPolicy: AccountingPolicy.init(),
  fetchContext: async () => {},
});

function CompanyProvider({ children }: { children: ReactNode }) {
  const [ctx, setCtx] = useState<{
    company: CompanyIdentity;
    articleSetting: ArticleSetting;
    accountingPolicy: AccountingPolicy;
  } | null>();

  const { tryRequest } = useTry();

  const fetchContext = useCallback(async () => {
    await tryRequest(async () => {
      setCtx(await fetchCompanyCtx());
    });
  }, []);

  useEffect(() => {
    void fetchContext();
  }, [fetchContext]);

  return !ctx ? null : (
    <CompanyContext.Provider
      value={{
        company: ctx.company,
        articleSetting: ctx.articleSetting,
        accountingPolicy: ctx.accountingPolicy,
        fetchContext,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

function useCompany() {
  return useContext(CompanyContext);
}

export { CompanyProvider, useCompany };
